<template>
  <div
    class="flex min-h-[100dvh] w-full overflow-x-hidden bg-zinc-100 text-zinc-900 dark:bg-[#05060a] dark:text-zinc-100 transition-colors font-sans antialiased">
    <div class="hidden md:flex flex-col shrink-0 h-screen sticky top-0 p-4 gap-4">
      <div class="h-16 flex-shrink-0 flex items-center gap-3 px-3 rounded-3xl border border-black/5 dark:border-white/10
               bg-white/90 dark:bg-[#111118] shadow-apple backdrop-blur">
        <div :class="[collapsed ? 'w-12' : 'w-40', 'h-auto']"
          class="flex items-center justify-start gap-2 transition-all duration-200 ease-in-out overflow-hidden">
          <div class="h-9 w-9 rounded-2xl grid place-items-center border border-black/5 dark:border-white/10
                   bg-zinc-50 dark:bg-[#05060a]">
            <img src="@/assets/16.png" alt="NexGridCo" class="h-7 w-7 object-contain rounded-xl block dark:hidden" />
            <img src="@/assets/1.png" alt="NexGridCo" class="h-7 w-7 object-contain rounded-xl hidden dark:block" />
          </div>
          <div v-if="!collapsed" class="space-y-0.5 select-none">
            <p class="text-xs font-semibold tracking-[0.22em] uppercase text-zinc-500 dark:text-zinc-400">
              NexGridCo
            </p>
            <p class="text-sm font-medium text-zinc-900 dark:text-zinc-50">
              Palette Studio
            </p>
          </div>
        </div>
        <button class="ml-auto rounded-xl p-2 hover:bg-black/5 dark:hover:bg-white/5" @click="toggleSidebar"
          :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'" :title="collapsed ? 'Expand' : 'Collapse'">
          <span class="material-symbols-outlined text-[20px]">
            {{ collapsed ? 'chevron_right' : 'chevron_left' }}
          </span>
        </button>
      </div>
      <aside :class="[
        'flex flex-col flex-1 border border-black/5 dark:border-white/10 bg-white/95 dark:bg-[#111118] transition-all duration-200 ease-in-out rounded-3xl overflow-hidden shadow-apple',
        collapsed ? 'md:w-20' : 'md:w-72'
      ]" role="navigation" aria-label="Main">
        <nav class="flex-1 overflow-y-auto custom-scroll">
          <div class="px-3 pt-3 pb-2">
            <p v-if="!collapsed"
              class="mb-2 text-[11px] font-medium uppercase tracking-[0.22em] text-zinc-400 dark:text-zinc-500">
              Overview
            </p>
            <button v-for="i in navAsidePrimary" :key="i.name"
              :class="[itemClass(i.name, i.thin ? 'thin' : ''), collapsed ? 'justify-center px-2' : '']"
              @click="goNamed(i.name)" :title="collapsed ? i.label : null">
              <span v-if="i.icon" class="material-symbols-outlined ms-icon">{{ i.icon }}</span>
              <img v-else-if="i.iconImg" :src="i.iconImg" :alt="i.label" class="h-9 w-9 object-contain" />
              <span v-if="!collapsed">{{ i.label }}</span>
            </button>
          </div>
          <div v-if="!collapsed" class="px-4 pt-2 pb-1">
            <p class="text-[11px] font-medium uppercase tracking-[0.22em] text-zinc-400 dark:text-zinc-500">
              Workspace
            </p>
          </div>
          <div v-else class="px-3 pt-3">
            <div class="h-px bg-black/10 dark:bg-white/10"></div>
          </div>
          <div class="px-2 space-y-1">
            <button v-for="i in navDoctorFonts" :key="i.name"
              :class="[itemClass(i.name, i.thin ? 'thin' : ''), collapsed ? 'justify-center px-2' : '']"
              @click="goNamed(i.name)" :title="collapsed ? i.label : null"
              :aria-disabled="isDisabled(i.name) ? 'true' : 'false'">
              <span v-if="i.icon" class="material-symbols-outlined ms-icon">{{ i.icon }}</span>
              <img v-else-if="i.iconImg" :src="i.iconImg" :alt="i.label" class="h-10 w-10 object-contain" />
              <span v-if="!collapsed">{{ i.label }}</span>
              <span v-if="!collapsed && i.badge"
                class="text-[10px] uppercase rounded-md bg-sky-600 text-white px-1.5 py-0.5 ml-auto">
                {{ i.badge }}
              </span>
            </button>
          </div>
          <div v-if="!collapsed" class="px-4 pt-5">
            <div class="flex items-center justify-between">
              <button
                class="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-medium text-zinc-500 dark:text-zinc-400 hover:bg-black/5 dark:hover:bg-white/5"
                @click="foldersOpen = !foldersOpen" :aria-expanded="foldersOpen ? 'true' : 'false'">
                <span class="uppercase tracking-[0.18em]">Folders</span>
                <span class="material-symbols-outlined text-[18px]">
                  {{ foldersOpen ? 'expand_less' : 'expand_more' }}
                </span>
              </button>
              <button
                class="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] border border-black/10 dark:border-white/15 hover:bg-black/5 dark:hover:bg-white/5"
                @click="openFolderModal = true" title="New folder">
                <span class="material-symbols-outlined text-[18px]">add</span>
                New
              </button>
            </div>
          </div>
          <div v-else class="px-3 pt-3">
            <div class="h-px bg-black/10 dark:bg-white/10"></div>
            <div class="mt-2 flex justify-center">
              <button
                class="h-9 w-9 grid place-items-center rounded-lg border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5"
                @click="openFolderModal = true" title="New folder" aria-label="New folder">
                <span class="material-symbols-outlined text-[20px]">add</span>
              </button>
            </div>
          </div>
          <ul class="px-2 space-y-1 pb-3" v-show="foldersOpen">
            <li v-for="f in visibleFolders" :key="f.key" class="group relative">
              <button class="nav-item thin w-full" :class="[collapsed ? 'justify-center px-2' : '']"
                @click="openGalleryFolder(f)" :title="collapsed ? f.label : null">
                <span class="material-symbols-outlined ms-icon">{{ f.icon }}</span>
                <span v-if="!collapsed" class="flex-1 text-left truncate">{{ f.label }}</span>
                <span v-if="!collapsed && typeof f.count === 'number'"
                  class="text-[11px] min-w-[28px] text-center rounded-md bg-black/5 dark:bg-white/5 ring-1 ring-black/10 dark:ring-white/10 px-1.5 py-0.5">
                  {{ f.count || 0 }}
                </span>
                <div v-if="!collapsed" class="relative ml-1 opacity-0 group-hover:opacity-100 transition">
                  <button class="h-7 w-7 grid place-items-center rounded-md hover:bg-black/10 dark:hover:bg:white/10"
                    @click.stop="toggleFolderMenu(f.key)">
                    <span class="material-symbols-outlined text-[18px]">more_horiz</span>
                  </button>
                  <div v-if="openFolderMenu === f.key"
                    class="absolute right-0 top-full mt-1 w-40 rounded-xl border border-black/10 dark:border-white/10 bg-white/95 dark:bg-[#111118] shadow-soft p-1 z-20">
                    <button
                      class="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-black/5 dark:hover:bg:white/5"
                      @click.stop="openIconPicker(f)">
                      <span class="material-symbols-outlined text-base">palette</span>
                      Icon
                    </button>
                    <button
                      class="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-black/5 dark:hover:bg:white/5"
                      @click.stop="renameFolder(f)">
                      <span class="material-symbols-outlined text-base">edit</span>
                      Rename
                    </button>
                    <div class="h-px bg-black/10 dark:bg-white/10 my-1"></div>
                    <button
                      class="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md text-sky-700 dark:text-sky-300 hover:bg-sky-600/10 dark:hover:bg-sky-600/20"
                      @click.stop="removeFolder(f)">
                      <span class="material-symbols-outlined text-base">delete</span>
                      Delete
                    </button>
                  </div>
                </div>
              </button>
            </li>
          </ul>
        </nav>
        <div class="mt-auto p-3 border-t border-black/5 dark:border-white/10 bg-white/95 dark:bg-[#0b0c10]">
          <div class="space-y-1">
            <button v-for="i in navAccount" :key="i.label"
              :class="['nav-item', i.thin ? 'thin' : '', collapsed ? 'justify-center px-2' : '']"
              :title="collapsed ? i.label : null" @click="i.onClick && i.onClick()">
              <span class="material-symbols-outlined ms-icon">{{ i.icon }}</span>
              <span v-if="!collapsed">{{ i.label }}</span>
            </button>
          </div>
        </div>
      </aside>
    </div>
    <div class="flex-1 min-w-0 flex flex-col relative">
      <div aria-hidden="true" class="pointer-events-none absolute inset-0 -z-10">
        <div
          class="absolute inset-0 bg-[radial-gradient(900px_520px_at_0%_0%,rgba(56,189,248,.16),transparent),radial-gradient(900px_520px_at_100%_0%,rgba(59,130,246,.16),transparent)] opacity-70 dark:opacity-50">
        </div>
      </div>
      <header class="sticky top-0 z-40">
        <div
          class="absolute inset-0 h-24 bg-gradient-to-b from-zinc-50/85 to-zinc-50/60 dark:from-[#05060a]/90 dark:to-[#05060a]/70 border-b border-black/5 dark:border-white/5 backdrop-blur-xl">
        </div>
        <div class="relative h-24 flex items-center justify-center px-3 md:px-6">
          <div
            class="toolbar-glass mx-auto w-full max-w-6xl h-14 md:h-16 rounded-2xl md:rounded-3xl ring-1 ring-black/5 dark:ring-white/10 bg-white/80 dark:bg-[#05060a]/80 shadow-apple grid grid-cols-[auto_1fr_auto] items-center gap-2 px-2 md:px-4">
            <div class="flex items-center gap-2 pl-1 min-w-0">
              <button class="md:hidden rounded-xl p-2 hover:bg-black/5 dark:hover:bg-white/5" @click="mobileOpen = true"
                aria-label="Open menu">
                <span class="material-symbols-outlined">menu</span>
              </button>
              <div class="flex items-center gap-2 min-w-0">
                <span class="chip-dot"></span>
                <div class="truncate">
                  <p class="text-xs uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-500">
                    Welcome
                  </p>
                  <h2 class="truncate text-sm md:text-base font-semibold tracking-tight">
                    {{ firstName }}
                  </h2>
                </div>
              </div>
            </div>
            <div class="hidden md:flex items-center justify-center">
              <div
                class="brand-pill inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ring-black/5 dark:ring-white/10 bg-zinc-50/80 dark:bg-[#111118]/90 text-xs md:text-sm">
                <span class="brand-pill-main">NexGridCo Studio</span>
                <span class="h-1 w-1 rounded-full bg-zinc-300 dark:bg-zinc-500"></span>
                <span class="brand-pill-sub text-zinc-500 dark:text-zinc-400">
                  Palettes &amp; Colors
                </span>
              </div>
            </div>
            <div class="flex items-center justify-end gap-1 md:gap-2 pr-1">
              <button class="rounded-xl px-2 py-1 text-sm hover:bg-black/5 dark:hover:bg:white/5" @click="toggleTheme"
                :aria-label="isDark ? 'Enable light theme' : 'Enable dark theme'">
                <span v-if="!isDark" class="material-symbols-outlined text-[20px]">light_mode</span>
                <span v-else class="material-symbols-outlined text-[20px]">dark_mode</span>
              </button>
              <div class="relative menu-root" @keydown.escape="menuOpen = false">
                <button @click="menuOpen = !menuOpen"
                  class="flex items-center gap-2 rounded-xl px-2 py-1 hover:bg-black/5 dark:hover:bg:white/5"
                  aria-haspopup="menu" :aria-expanded="menuOpen ? 'true' : 'false'">
                  <div
                    class="h-9 w-9 rounded-full bg-gradient-to-br from-sky-500 to-cyan-400 grid place-items-center font-semibold text-white text-sm shadow-[0_10px_25px_rgba(56,189,248,.45)]">
                    {{ userInitials }}
                  </div>
                  <div class="hidden sm:block text-left">
                    <p class="text-sm font-medium -mb-0.5 truncate max-w-[160px]">
                      {{ userNameDisplay }}
                    </p>
                    <p class="text-[11px] text-zinc-500 dark:text-zinc-400">
                      Palette workspace
                    </p>
                  </div>
                  <span class="material-symbols-outlined text-zinc-400 text-[20px]">expand_more</span>
                </button>
                <div v-show="menuOpen"
                  class="absolute right-0 mt-2 w-[min(20rem,calc(100vw-2.5rem))] sm:w-80 origin-top-right rounded-2xl border border-black/10 dark:border-white/10 bg-white/95 dark:bg-[#05060a] shadow-soft p-2 z-50"
                  role="menu">
                  <div
                    class="px-3.5 py-3 rounded-xl bg-gradient-to-br from-sky-500/15 via-sky-600/10 to-cyan-400/10 flex items-center gap-3 mb-2">
                    <div
                      class="h-9 w-9 rounded-full bg-gradient-to-br from-sky-500 to-cyan-400 grid place-items-center text-xs font-semibold text-white shadow-[0_10px_25px_rgba(56,189,248,.55)]">
                      {{ userInitials }}
                    </div>
                    <div class="min-w-0">
                      <p class="text-[11px] font-medium uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-400">
                        Signed in
                      </p>
                      <p class="text-[13px] font-semibold text-zinc-900 dark:text-zinc-50 truncate">
                        {{ userNameDisplay }}
                      </p>
                      <p class="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">
                        {{ userEmail }}
                      </p>
                    </div>
                  </div>
                  <div class="px-2 pt-1 pb-2 space-y-1.5">
                    <button
                      class="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm hover:bg-zinc-100/90 dark:hover:bg-zinc-900/80 text-zinc-800 dark:text-zinc-100"
                      @click="goNamed(ROUTE.assinatura)" role="menuitem">
                      <div
                        class="h-8 w-8 rounded-xl grid place-items-center bg-sky-500/10 text-sky-600 dark:text-sky-300">
                        <span class="material-symbols-outlined text-[18px]">credit_card</span>
                      </div>
                      <div class="flex-1 text-left">
                        <p class="font-medium leading-tight">Subscription</p>
                        <p class="text-[11px] text-zinc-500 dark:text-zinc-400">
                          Manage your plan and billing
                        </p>
                      </div>
                      <span
                        class="ml-1 inline-flex items-center rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.16em] text-sky-600 dark:text-sky-300">
                        Studio
                      </span>
                    </button>
                    <button
                      class="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-500/10"
                      @click="logout" role="menuitem">
                      <div
                        class="h-8 w-8 rounded-xl grid place-items-center bg-rose-500/10 text-rose-500 dark:text-rose-300">
                        <span class="material-symbols-outlined text-[18px]">logout</span>
                      </div>
                      <div class="flex-1 text-left">
                        <p class="font-medium leading-tight">Logout</p>
                        <p class="text-[11px] text-rose-500/80 dark:text-rose-200/80">
                          End session on this device
                        </p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main class="px-4 md:px-6 py-6">
        <div :class="[
          'w-full mx-auto space-y-4 md:space-y-6 transition-all duration-300',
          collapsed ? 'max-w-[1680px]' : 'max-w-6xl'
        ]">
          <router-view />
        </div>
      </main>
      <footer class="mt-auto px-4 md:px-6 pb-5 text-xs md:text-sm text-zinc-500 dark:text-zinc-500">
        <div class="flex items-center justify-center">
          <p>&copy; {{ new Date().getFullYear() }} NexGridCo. All rights reserved.</p>
        </div>
      </footer>
    </div>
    <transition name="fade">
      <div v-if="mobileOpen" class="fixed inset-0 z-40 md:hidden">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="mobileOpen = false"></div>
        <div
          class="absolute left-0 top-0 h-full w-[82vw] max-w-xs bg-white dark:bg-[#05060a] border-r border-black/10 dark:border-white/10 shadow-2xl">
          <div class="h-16 flex items-center justify-between px-4 border-b border-black/10 dark:border-white/10">
            <div class="flex items-center gap-2">
              <div
                class="h-9 w-9 rounded-2xl grid place-items-center border border-black/10 dark:border-white/10 bg-zinc-50 dark:bg-[#05060a]">
                <img src="@/assets/16.png" alt="NexGridCo"
                  class="h-7 w-7 object-contain rounded-xl block dark:hidden" />
                <img src="@/assets/1.png" alt="NexGridCo" class="h-7 w-7 object-contain rounded-xl hidden dark:block" />
              </div>
              <div class="leading-tight">
                <p class="text-xs font-semibold tracking-[0.22em] uppercase text-zinc-500 dark:text-zinc-400">
                  NexGridCo
                </p>
                <p class="text-sm font-medium text-zinc-900 dark:text-zinc-50">Palette Studio</p>
              </div>
            </div>
            <button class="rounded-lg p-2 hover:bg-black/5 dark:hover:bg:white/5" @click="mobileOpen = false"
              aria-label="Close menu">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <nav class="h-[calc(100%-4rem)] overflow-y-auto custom-scroll">
            <div class="p-3 space-y-1">
              <button v-for="i in navAsidePrimary" :key="i.name" class="nav-item"
                :class="itemClass(i.name, i.thin ? 'thin' : '')" @click="tapGo(i.name)">
                <span v-if="i.icon" class="material-symbols-outlined ms-icon">{{ i.icon }}</span>
                <img v-else-if="i.iconImg" :src="i.iconImg" :alt="i.label" class="h-8 w-8 object-contain" />
                <span>{{ i.label }}</span>
              </button>
            </div>
            <div class="px-4 pt-4 pb-1">
              <p class="text-xs uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-400 mb-2">
                Workspace
              </p>
            </div>
            <div class="px-2 space-y-1">
              <button v-for="i in navDoctorFontsMobile" :key="i.name" class="nav-item"
                :class="itemClass(i.name, i.thin ? 'thin' : '')" @click="tapGo(i.name)"
                :aria-disabled="isDisabled(i.name) ? 'true' : 'false'">
                <span v-if="i.icon" class="material-symbols-outlined ms-icon">{{ i.icon }}</span>
                <img v-else-if="i.iconImg" :src="i.iconImg" :alt="i.label" class="h-8 w-8 object-contain" />
                <span>{{ i.label }}</span>
                <span v-if="i.badge"
                  class="text-[10px] uppercase rounded-md bg-sky-600 text-white px-1.5 py-0.5 ml-auto">
                  {{ i.badge }}
                </span>
              </button>
            </div>
            <div class="px-4 pt-5">
              <p class="text-xs uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-400 mb-2">
                Account
              </p>
            </div>
            <div class="px-2 space-y-1 mb-6">
              <button v-for="i in navAccount" :key="i.label" class="nav-item" :class="[i.thin ? 'thin' : '']"
                @click="(i.onClick && i.onClick(), mobileOpen = false)">
                <span class="material-symbols-outlined ms-icon">{{ i.icon }}</span>
                <span>{{ i.label }}</span>
              </button>
            </div>
          </nav>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div v-if="renameModalOpen" class="fixed inset-0 z-50">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" @click="closeRenameModal"></div>
        <div
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-md rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 bg-white dark:bg-[#05060a] shadow-2xl">
          <div class="p-5 md:p-6 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
            <h3 class="text-base sm:text-lg font-semibold">Rename folder</h3>
            <button class="rounded-lg p-2 hover:bg-black/5 dark:hover:bg:white/5" @click="closeRenameModal">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <div class="p-5 md:p-6 space-y-5">
            <div>
              <label class="text-xs text-zinc-500 dark:text-zinc-400">New name</label>
              <input v-model.trim="renameName" type="text"
                class="mt-1 w-full rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#05060a] px-3 py-2 text-sm"
                :placeholder="renameTarget?.label || ''" @keyup.enter="confirmRename" />
            </div>
            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2">
              <button
                class="flex-1 rounded-xl px-4 py-2 text-sm font-semibold bg-zinc-900 text-white dark:bg-sky-600 disabled:opacity-40"
                :disabled="renaming || !renameName.trim() || renameName.trim() === renameTarget?.label"
                @click="confirmRename">
                Rename
              </button>
              <button
                class="rounded-xl px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
                @click="closeRenameModal">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div v-if="openFolderModal" class="fixed inset-0 z-50">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" @click="closeFolderModal"></div>
        <div
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-2xl rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 bg-white dark:bg-[#05060a] shadow-2xl">
          <div class="p-5 md:p-6 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
            <h3 class="text-base sm:text-lg font-semibold">New folder</h3>
            <button class="rounded-lg p-2 hover:bg-black/5 dark:hover:bg:white/5" @click="closeFolderModal">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <div class="p-5 md:p-6 space-y-5">
            <div>
              <label class="block text-xs text-zinc-500 dark:text-zinc-400">Name</label>
              <input v-model.trim="newFolderName" type="text"
                class="mt-1 w-full rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#05060a] px-3 py-2 text-sm"
                placeholder="e.g., Brand Concepts" @keyup.enter="createFolder" />
            </div>
            <div>
              <label class="block text-xs text-zinc-500 dark:text-zinc-400 mb-2">Icon</label>
              <div class="grid grid-cols-8 gap-2">
                <button v-for="ic in iconChoices" :key="ic" @click="newFolderIcon = ic" :class="[
                  'h-10 rounded-lg border grid place-items-center',
                  newFolderIcon === ic
                    ? 'border-sky-500 ring-2 ring-sky-300/60'
                    : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg:white/5'
                ]">
                  <span class="material-symbols-outlined">{{ ic }}</span>
                </button>
              </div>
            </div>
            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2">
              <button
                class="flex-1 rounded-xl px-4 py-2 text-sm font-semibold bg-zinc-900 text-white dark:bg-sky-600 disabled:opacity-40"
                :disabled="!newFolderName" @click="createFolder">
                Create
              </button>
              <button
                class="rounded-xl px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
                @click="closeFolderModal">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div v-if="iconPickerOpen" class="fixed inset-0 z-50">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" @click="closeIconPicker"></div>
        <div
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-md rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 bg-white dark:bg-[#05060a] shadow-2xl">
          <div class="p-5 md:p-6 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
            <h3 class="text-base sm:text-lg font-semibold">Choose icon</h3>
            <button class="rounded-lg p-2 hover:bg-black/5 dark:hover:bg:white/5" @click="closeIconPicker">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <div class="p-5 md:p-6 space-y-5">
            <div class="grid grid-cols-8 gap-2">
              <button v-for="ic in iconChoices" :key="ic" @click="pickedIcon = ic" :class="[
                'h-10 rounded-lg border grid place-items-center',
                pickedIcon === ic
                  ? 'border-sky-500 ring-2 ring-sky-300/60'
                  : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg:white/5'
              ]">
                <span class="material-symbols-outlined">{{ ic }}</span>
              </button>
            </div>
            <div class="flex items-center justify-end gap-2 pt-2">
              <button
                class="rounded-xl px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
                @click="closeIconPicker">
                Cancel
              </button>
              <button class="rounded-xl px-4 py-2 text-sm font-semibold bg-zinc-900 text-white dark:bg-sky-600"
                @click="applyIcon">
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div v-if="supportOpen" class="fixed inset-0 z-50">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" @click="closeSupportModal"></div>
        <div
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-xl rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 bg-white dark:bg-[#05060a] shadow-2xl">
          <div class="relative p-5 md:p-6 support-hero border-b border-black/10 dark:border-white/10">
            <div
              class="absolute -inset-24 bg-[radial-gradient(40rem_28rem_at_85%_-10%,rgba(56,189,248,.22),transparent),radial-gradient(32rem_18rem_at_10%_-10%,rgba(59,130,246,.18),transparent)]">
            </div>
            <div class="relative flex items-center gap-3">
              <div
                class="h-9 w-9 rounded-xl grid place-items-center ring-1 ring-black/10 dark:ring-white/10 bg-white/90 dark:bg-[#05060a]">
                <img src="@/assets/16.png" alt="NexGridCo"
                  class="h-7 w-7 rounded-lg object-contain block dark:hidden" />
                <img src="@/assets/1.png" alt="NexGridCo" class="h-7 w-7 rounded-lg object-contain hidden dark:block" />
              </div>
              <div class="leading-tight">
                <h3 class="text-lg sm:text-xl font-semibold tracking-tight">Contact support</h3>
                <p class="text-[12px] text-zinc-500 dark:text-zinc-400">
                  Send your question and we’ll open your email pre-filled.
                </p>
              </div>
              <button class="ml-auto rounded-lg p-2 hover:bg-black/5 dark:hover:bg:white/5" @click="closeSupportModal"
                aria-label="Close">
                <span class="material-symbols-outlined">close</span>
              </button>
            </div>
          </div>
          <div class="p-5 md:p-6 space-y-4">
            <div>
              <label class="text-xs text-zinc-500 dark:text-zinc-400">Name</label>
              <div class="relative mt-1">
                <span class="material-symbols-outlined input-leading-icon">person</span>
                <input v-model.trim="supportName" type="text"
                  class="w-full rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#05060a] px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label class="text-xs text-zinc-500 dark:text-zinc-400">Email</label>
              <div class="relative mt-1">
                <span class="material-symbols-outlined input-leading-icon">alternate_email</span>
                <input v-model.trim="supportEmailInput" type="email" :class="[
                  'w-full rounded-xl border bg-white dark:bg-[#05060a] px-3 py-2 pl-10 text-sm',
                  supportEmailInput && !supportEmailValid
                    ? 'border-rose-500/70 ring-1 ring-rose-500/40'
                    : 'border-black/10 dark:border-white/10'
                ]" />
              </div>
              <p v-if="supportEmailInput && !supportEmailValid"
                class="mt-1 text-[11px] text-rose-600 dark:text-rose-300">
                Enter a valid email.
              </p>
            </div>
            <div>
              <label class="text-xs text-zinc-500 dark:text-zinc-400">Message</label>
              <div class="relative mt-1">
                <span class="material-symbols-outlined input-leading-icon top-3">edit_note</span>
                <textarea v-model.trim="supportMsg" rows="5"
                  class="w-full rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#05060a] px-3 py-2 pl-10 text-sm"
                  placeholder="Describe your question or issue"></textarea>
              </div>
            </div>
            <div class="flex items-center justify-between text-[12px] text-zinc-500 dark:text-zinc-400">
              <a :href="`mailto:${SUPPORT_EMAIL}`" class="hover:underline truncate">
                Or email directly at {{ SUPPORT_EMAIL }}
              </a>
              <span>{{ supportMsg.trim().length }}/1000</span>
            </div>
            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
              <button
                class="flex-1 rounded-xl px-4 py-2 text-sm font-semibold bg-zinc-900 text-white dark:bg-sky-600 disabled:opacity-40"
                :disabled="!supportReady" @click="submitSupport">
                Send email
              </button>
              <button
                class="rounded-xl px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
                @click="closeSupportModal">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import AuthService from '@/service/authService'
const router = useRouter()
const route = useRoute()
const openFolderMenu = ref(null)
function toggleFolderMenu(key) {
  openFolderMenu.value = openFolderMenu.value === key ? null : key
}
const ROUTE = Object.freeze({
  inicio: 'inicio',
  cores: 'cores',
  galeria: 'galeria',
  assinatura: 'assinatura',
  studio: 'studio',
  tester: 'studio-tester',
  create: 'create',
  semantic: 'semantic',
  accessibility: 'accessibility',
  iacolor: 'iacolor',
  ask: 'ask',
  texture: 'texture',
  hub: 'hub'
})
const navAsidePrimary = [{ name: ROUTE.inicio, icon: 'home', label: 'Dashboard' }]
const navDoctorFonts = [
  { name: ROUTE.iacolor, icon: 'wand_shine', label: 'Generate', badge: 'IA' },
  { name: ROUTE.ask, icon: 'star_shine', label: 'AI Chat' },
  { name: ROUTE.texture, icon: 'texture', label: 'Texture Studio' },
  { name: ROUTE.studio, icon: 'brush', label: 'Palette Studio', badge: 'Templates' },
  { name: ROUTE.cores, icon: 'palette', label: 'Sky Studio', thin: true },
  { name: ROUTE.create, icon: 'gradient', label: 'Gradient Craft' },
  { name: ROUTE.accessibility, icon: 'match_case', label: 'Accessibility' },
  { name: ROUTE.semantic, icon: 'colors', label: 'Token Studio' },
  { name: ROUTE.tester, icon: 'check_circle', label: 'UI Playground', badge: 'Beta' },
  { name: ROUTE.hub, icon: 'cloud', label: 'NexGridCo Hub' },
  { name: ROUTE.galeria, icon: 'folder_check', label: 'Library' },
]

const navDoctorFontsMobile = computed(() => navDoctorFonts.filter(i => i.name !== ROUTE.tester))
const disabledRoutes = new Set()
const isDisabled = (name) => disabledRoutes.has(name)
const SUPPORT_EMAIL = process.env.VUE_APP_SUPPORT_EMAIL || 'contact@nexgridco.com'
const supportOpen = ref(false)
const supportName = ref('')
const supportEmailInput = ref('')
const supportMsg = ref('')
function openSupportModal() {
  supportName.value = userNameDisplay.value || ''
  supportEmailInput.value = userEmail.value || ''
  supportMsg.value = ''
  supportOpen.value = true
}
function closeSupportModal() {
  supportOpen.value = false
}
function submitSupport() {
  const name = (supportName.value || 'Usuário').trim()
  const emailVal = (supportEmailInput.value || '').trim()
  const subject = `NexGridCo • Support • ${name}`
  const body = [
    'Hello team NexGridCo,',
    '',
    supportMsg.value?.trim() || '(sem mensagem)',
    '',
    `Nome: ${name}`,
    `E-mail: ${emailVal || 'sem-email'}`,
    `Rota: ${route.fullPath}`,
    `Data: ${new Date().toISOString()}`
  ].join('\n')
  const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
    body
  )}`
  window.location.href = href
  supportOpen.value = false
}
const supportEmailValid = computed(() => {
  const v = (supportEmailInput.value || '').trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
})
const supportReady = computed(
  () => (supportMsg.value || '').trim().length > 0 && supportEmailValid.value
)
const navAccount = [{ icon: 'support_agent', label: 'Support', thin: true, onClick: openSupportModal }]
const isActive = (key) => {
  if (!key) return false
  if (key === ROUTE.texture) {
    return route.name === 'texture' || route.name === 'texture-project'
  }
  return route.name === key
}
const itemClass = (n, extra = '') =>
  ['nav-item', extra, n && isActive(n) ? 'nav-active' : '', n && canGo(n) ? '' : 'nav-disabled']
    .filter(Boolean)
    .join(' ')
const canGo = (name) => !!name && router.hasRoute(name) && !isDisabled(name)
function goNamed(name) {
  if (name && router.hasRoute(name) && !isDisabled(name) && route.name !== name) router.push({ name })
}
const tapGo = (name) => {
  if (canGo(name)) {
    goNamed(name)
    mobileOpen.value = false
  }
}
const openFolderModal = ref(false)
const newFolderName = ref('')
const newFolderIcon = ref('folder')
const iconChoices = [
  'folder',
  'text_fields',
  'palette',
  'brush',
  'font_download',
  'bolt',
  'layers',
  'collections',
  'tag',
  'emoji_objects',
  'work',
  'star',
  'favorite',
  'lab_profile',
  'image',
  'format_size'
]
const folderIconMap = ref({})
function loadFolderIcons() {
  try {
    folderIconMap.value = JSON.parse(localStorage.getItem('df.galleryFolderIcons') || '{}') || {}
  } catch (e) {
    return
  }
}
function saveFolderIcons() {
  localStorage.setItem('df.galleryFolderIcons', JSON.stringify(folderIconMap.value))
}
const galleryFolders = ref([])
const visibleFolders = computed(() => galleryFolders.value)
async function fetchGalleryFolders() {
  try {
    const arr = await import('@/api/images').then((m) => m.listGroups())
    galleryFolders.value =
      arr?.map((g) => ({
        key: g.name,
        label: g.name,
        icon: folderIconMap.value[g.name] || 'folder',
        count: g.count || 0
      })) || []
  } catch (e) {
    galleryFolders.value = []
    return
  }
}
function closeFolderModal() {
  openFolderModal.value = false
  newFolderName.value = ''
  newFolderIcon.value = 'folder'
}
async function createFolder() {
  const label = newFolderName.value.trim()
  if (!label) return
  try {
    const api = await import('@/api/images')
    await api.createGroup(label)
    folderIconMap.value[label] = newFolderIcon.value || 'folder'
    saveFolderIcons()
    await fetchGalleryFolders()
    closeFolderModal()
  } catch (e) {
    return
  }
}
const renameModalOpen = ref(false)
const renameTarget = ref(null)
const renameName = ref('')
const renaming = ref(false)
function renameFolder(f) {
  renameTarget.value = f
  renameName.value = f.label
  renameModalOpen.value = true
}
function closeRenameModal() {
  if (renaming.value) return
  renameModalOpen.value = false
  renameTarget.value = null
  renameName.value = ''
}
async function confirmRename() {
  const next = renameName.value.trim()
  if (!renameTarget.value || !next || next === renameTarget.value.label) {
    closeRenameModal()
    return
  }
  renaming.value = true
  try {
    const api = await import('@/api/images')
    await api.renameGroup(renameTarget.value.label, next)
    const icon = folderIconMap.value[renameTarget.value.label]
    if (icon) {
      delete folderIconMap.value[renameTarget.value.label]
      folderIconMap.value[next] = icon
      saveFolderIcons()
    }
    await fetchGalleryFolders()
    if (route.name === 'galeria' && route.params.gid === renameTarget.value.label) {
      router.replace({ name: 'galeria', params: { gid: next } })
    }
  } catch (e) {
    //
  } finally {
    renaming.value = false
    closeRenameModal()
  }
}
async function removeFolder(f) {
  if (!confirm(`Delete "${f.label}" and its files?`)) return
  try {
    const api = await import('@/api/images')
    await api.deleteGroup(f.label)
    if (folderIconMap.value[f.label]) {
      delete folderIconMap.value[f.label]
      saveFolderIcons()
    }
    await fetchGalleryFolders()
  } catch (e) {
    return
  }
}
function openGalleryFolder(f) {
  const gid = f.key || f.label
  if (route.name === 'galeria' && route.params.gid === gid) return
  router.push({ name: 'galeria', params: { gid } })
}
const iconPickerOpen = ref(false)
const editingIconFor = ref(null)
const pickedIcon = ref('folder')
function openIconPicker(f) {
  editingIconFor.value = f.label
  pickedIcon.value = folderIconMap.value[f.label] || 'folder'
  iconPickerOpen.value = true
}
function closeIconPicker() {
  iconPickerOpen.value = false
  editingIconFor.value = null
}
function applyIcon() {
  if (!editingIconFor.value) return
  folderIconMap.value[editingIconFor.value] = pickedIcon.value || 'folder'
  saveFolderIcons()
  const item = galleryFolders.value.find((x) => x.label === editingIconFor.value)
  if (item) item.icon = folderIconMap.value[editingIconFor.value]
  closeIconPicker()
}
const isMobile = ref(false)
const checkScreenSize = () => {
  isMobile.value = window.innerWidth < 640
}
onMounted(() => {
  checkScreenSize()
  window.addEventListener('resize', checkScreenSize)
})
onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize)
})
const user = ref(null)
function loadUser() {
  try {
    const storage = localStorage.getItem('token') ? localStorage : sessionStorage
    const token = storage.getItem('token')
    const userStr = storage.getItem('user')
    const email = storage.getItem('email') || ''
    const name = storage.getItem('name') || ''
    const parsed = userStr ? JSON.parse(userStr) : null
    user.value = {
      token,
      email: parsed?.email || email,
      name: parsed?.name || name,
      username: parsed?.username || email
    }
  } catch (e) {
    user.value = null
    return
  }
}
const userEmail = computed(() => user.value?.email || '')
const userNameDisplay = computed(
  () => user.value?.name || user.value?.username || 'User'
)
const firstName = computed(
  () => userNameDisplay.value?.split?.(' ')[0] || 'User'
)
const userInitials = computed(() => {
  const n = (user.value?.name || user.value?.username || 'U').trim()
  const parts = n.split(/\s+/)
  const a = parts[0]?.[0] || 'U'
  const b = parts[1]?.[0] || parts[0]?.[1] || ''
  return (a + b).toUpperCase()
})
function dateOnlyFromISOish(raw) {
  const m = String(raw).match(/(\d{4})-(\d{2})-(\d{2})/)
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const d = new Date(raw)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}
const userCreatedAt = ref(null)
async function ensureUserCreatedAt() {
  const storage = localStorage.getItem('token') ? localStorage : sessionStorage
  const saved = storage.getItem('createdAtDateOnly') || localStorage.getItem('createdAtDateOnly')
  if (saved) {
    const d = dateOnlyFromISOish(saved)
    if (!isNaN(d)) {
      userCreatedAt.value = d
      return
    }
  }
  const me = await AuthService.me()
  const raw = me?.createdAt || me?.user?.createdAt || me?.created_at
  let d = dateOnlyFromISOish(raw)
  if (isNaN(d)) {
    const token = (localStorage.getItem('token') ? localStorage : sessionStorage).getItem('token')
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] || ''))
        if (payload?.iat) d = dateOnlyFromISOish(new Date(payload.iat * 1000).toISOString())
      } catch (e) {
        return
      }
    }
  }
  if (!isNaN(d)) {
    userCreatedAt.value = d
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    const ymd = `${y}-${m}-${dd}`
    localStorage.setItem('createdAtDateOnly', ymd)
    storage.setItem('createdAtDateOnly', ymd)
  }
}
const isDark = ref(false)
function applyTheme() {
  const root = document.documentElement
  if (isDark.value) root.classList.add('dark')
  else root.classList.remove('dark')
}
function toggleTheme() {
  isDark.value = !isDark.value
}
watch(isDark, (v) => {
  localStorage.setItem('theme', v ? 'dark' : 'light')
  applyTheme()
})
const mobileOpen = ref(false)
const menuOpen = ref(false)
function logout() {
  menuOpen.value = false
  AuthService.logout()
  router.push('/')
}
const collapsed = ref(false)
function toggleSidebar() {
  collapsed.value = !collapsed.value
  localStorage.setItem('sidebarCollapsed', collapsed.value ? '1' : '0')
}
const FOLDERS_OPEN_KEY = 'df.ui.foldersOpen'
const foldersOpen = ref(true)
function loadSectionState() {
  const v = localStorage.getItem(FOLDERS_OPEN_KEY)
  foldersOpen.value = v == null ? true : v === '1'
}
watch(foldersOpen, (v) => localStorage.setItem(FOLDERS_OPEN_KEY, v ? '1' : '0'))
function onDocClick(e) {
  const inMenu = e.target?.closest?.('.menu-root')
  if (!inMenu) menuOpen.value = false
}
onMounted(() => {
  const savedTheme = localStorage.getItem('theme')
  isDark.value = savedTheme ? savedTheme === 'dark' : !!window.matchMedia?.('(prefers-color-scheme: dark)').matches
  applyTheme()
  loadUser()
  ensureUserCreatedAt()
  document.addEventListener('click', onDocClick)
  const savedSidebar = localStorage.getItem('sidebarCollapsed')
  collapsed.value = savedSidebar === '1'
  loadFolderIcons()
  fetchGalleryFolders()
  window.addEventListener('df:gallery-refresh', fetchGalleryFolders)
  loadSectionState()
})
onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  window.removeEventListener('df:gallery-refresh', fetchGalleryFolders)
})
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');

.custom-scroll::-webkit-scrollbar {
  width: 10px;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.dark .custom-scroll::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.12);
}

.custom-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.ms-icon {
  font-size: 1.4rem;
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20;
  line-height: 1;
  vertical-align: middle;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 0.8rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  transition: background 0.18s, color 0.18s, box-shadow 0.18s, transform 0.12s;
}

.dark .nav-item {
  color: #e5e7eb;
}

.nav-item:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #0f172a;
}

.dark .nav-item:hover {
  background: rgba(148, 163, 184, 0.1);
  color: #f9fafb;
}

.nav-item.thin {
  padding: 0.5rem 0.75rem;
}

.nav-active {
  color: #0284c7;
  background: linear-gradient(135deg,
      rgba(56, 189, 248, 0.14),
      rgba(59, 130, 246, 0.18));
  box-shadow: 0 8px 18px -6px rgba(56, 189, 248, 0.55);
  transform: translateY(-1px);
}

.dark .nav-active {
  color: #bae6fd;
  background: linear-gradient(135deg,
      rgba(56, 189, 248, 0.22),
      rgba(59, 130, 246, 0.3));
  box-shadow: 0 10px 22px -8px rgba(56, 189, 248, 0.6);
}

.nav-disabled {
  opacity: 0.55;
  pointer-events: none;
}

.toolbar-glass {
  position: relative;
}

.shadow-apple {
  box-shadow: 0 12px 35px rgba(15, 23, 42, 0.18);
}

.chip-dot {
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: conic-gradient(from 140deg, #22c55e, #4ade80, #22c55e);
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.22);
}

.shadow-soft {
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.18);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.support-hero {
  background: linear-gradient(180deg,
      rgba(255, 255, 255, 0.96),
      rgba(248, 250, 252, 0.96));
}

.dark .support-hero {
  background: linear-gradient(180deg,
      rgba(15, 23, 42, 0.96),
      rgba(15, 23, 42, 0.9));
}

.input-leading-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  opacity: 0.6;
  pointer-events: none;
}

.input-leading-icon.top-3 {
  top: 0.85rem;
  transform: none;
}

.brand-pill,
.brand-pill-main,
.brand-pill-sub {
  font-family: 'Bebas Neue', system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Text', ui-sans-serif, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.16em;
}

.brand-pill-main {
  font-size: 0.9rem;
}

.brand-pill-sub {
  font-size: 0.8rem;
}

:global(html),
:global(body),
:global(#app) {
  margin: 0;
  padding: 0;
  width: 100%;
  min-width: 100%;
  min-height: 100%;
  border: 0;
  outline: 0;
  box-shadow: none;
}

:global(html),
:global(body),
:global(#app) {
  background: #f4f4f5;
}

:global(html.dark),
:global(html.dark body),
:global(html.dark #app) {
  background: #05060a;
}

:global(body) {
  overflow-x: hidden;
}

:global(*),
:global(*::before),
:global(*::after) {
  box-sizing: border-box;
}

</style>
